import { Icon, Typography } from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Step } from 'react-joyride'
import NewButton from 'ui/atoms/NewButton/NewButton'
import { systemFeatureFlagActive } from 'utils/systemUtils'
import { useAuth } from '../AuthContext/AuthContext'
import Tour from '../Tour/Tour'
import StaticFilterDropdown from './StaticFilterDropdown'

interface FilterTourProps {
  runTour: null | number
  setRunTour: (state: boolean) => void
}

const FilterTour = ({ runTour, setRunTour }: FilterTourProps) => {
  const { t } = useTranslation()
  const { activeSystem } = useAuth()

  const hasSavedFilterFeature = systemFeatureFlagActive(activeSystem, 'saved_filter_component')

  const steps: Array<Step> = [
    {
      target: 'filter_group',
      placement: 'top',
      content: (
        <div>
          <Typography variant="h6">
            {t(
              'Welcome to the new filter tool! With these dropdown menus, you can easily display exactly the information you are interested in seeing.'
            )}
          </Typography>
          <br />
          <span className="flex justify-center">
            <StaticFilterDropdown
              label={t('Example filter')}
              options={[`${t('Alternative')} A`, `${t('Alternative')} B`]}
            />
          </span>
          <br />
          <Typography variant="h6">
            <span className="border border-vercel-primary rounded-md px-1 text-vercel-primary font-sans font-normal">
              {3}
            </span>{' '}
            <b>{`${t('Filtering')}:`}</b>{' '}
            {t(
              'Filtering is activated when you have selected the options you are interested in and click ( Apply ) or outside the dropdown menu. If you have not selected any options, all data is shown by default. The number 3 here symbolizes that 3 filters have been selected. The dropdown menus are dependent on each other and are automatically updated to show the options that are relevant based on your previous selections.'
            )}
          </Typography>
          <br />
          <Typography variant="h6">
            <i>{`${t('Example')}:`}</i>{' '}
            {t(
              'I open the filter for Net and select Net Area 1 and Network Area 3. When I then click ( Apply ), I will only see data related to Network Area 1 and Network Area 3 in the module.'
            )}
          </Typography>
          <br />
          <Typography variant="h6">
            <span className="border border-vercel-primary rounded-md px-1">
              {/* @ts-ignore: It states extra-small doesn't exists even when it does*/}
              <Icon className="fas fa-layer-group" fontSize="extra-small" color="primary" />
            </span>{' '}
            <b>{`${t('Grouping')}:`}</b>{' '}
            {t(
              'This symbol indicates that a filter is grouped. Grouping a filter means that all data linked to the options from this filter is aggregated per option and displayed as a single unit (line or data point) in graphs and tables. Simply put, it gives you a different way to view and summarize the data!'
            )}
          </Typography>
          <br />
          <Typography variant="h6">
            <i>{`${t('Example')}:`}</i>{' '}
            {t(
              'We have a unit Boiler 1 and a unit Boiler 2, both of which use the fuel Waste. If we then group the Fuel filter, the data from Boiler 1 and Boiler 2 will be aggregated and displayed as Waste in the graphs and tables.'
            )}
          </Typography>
        </div>
      ),
      title: t('How does the filtering work?'),
    },
    {
      target: 'filter_group',
      content: (
        <div>
          <Typography variant="h6">
            <b>{`${t('Saved filtering')}:`}</b>{' '}
            {t(
              'Your filter selections are now saved automatically across all modules in the application — and they are retained even if you reload the page. This means you can navigate freely and focus on your work without having to set up the filters again when moving between pages.'
            )}
          </Typography>
          <br />
          <Typography variant="h6">
            <b>{`${t('Reset filtering')}:`}</b> {t('If you want to return to the original filtering, click on')}
            <NewButton variant="outlined" label={t('Reset all filters')} onClick={() => void 0} />
            {t(
              '(which is only visible if you have applied filters), then all filters will be unchecked and you will see all the data available in this module again.'
            )}
          </Typography>
          {hasSavedFilterFeature && (
            <>
              <br />
              <Typography variant="h6">
                <b>{`${t('My saved filters')}:`}</b>{' '}
                {t(
                  `In the dropdown 'My saved filters' you can save filters for future use, allowing you to quickly apply the filters you use most frequently. These filters are personal and will only be visible to you.`
                )}
              </Typography>
            </>
          )}
        </div>
      ),
      title: t('How does the filtering work?'),
    },
  ]

  return (
    <Tour
      isSingle={!!runTour}
      run={runTour !== null}
      setRunState={setRunTour}
      steps={steps}
      dialogWidth={window.innerWidth < 1280 ? 1200 : undefined}
    />
  )
}

export default FilterTour
